import React from 'react';
import { Paper, Stack, Typography, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

interface Props {
  description?: string;
  saveLabel?: string;
  onSave: () => void | Promise<void>;
  onReset?: () => void;
  saving?: boolean;
  disabled?: boolean;
}

export default function StickySaveBar({
  description,
  saveLabel = 'Save Changes',
  onSave,
  onReset,
  saving = false,
  disabled = false,
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Paper
      elevation={0}
      sx={(t) => ({
        position: 'sticky',
        bottom: 0,
        left: 0,
        right: 0,
        p: { xs: 1.5, sm: 2.5 },
        mt: 3,
        borderRadius: `${t.ds.radius.md}px`,
        bgcolor: t.tokens.surface.raised,
        borderTop: `1px solid ${t.tokens.border.default}`,
        zIndex: 10,
        boxShadow: t.tokens.shadow.lg,
      })}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        justifyContent="flex-end"
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        {description ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mr: { sm: 'auto' },
              textAlign: { xs: 'center', sm: 'left' },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              lineHeight: 1.4,
            }}
          >
            {description}
          </Typography>
        ) : null}
        <Stack direction="row" spacing={1.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          {onReset ? (
            <Button
              variant="outlined"
              onClick={onReset}
              disabled={saving}
              size={isMobile ? 'medium' : 'large'}
              sx={{ flex: { xs: 1, sm: 'none' }, textTransform: 'none', fontWeight: 600 }}
            >
              Reset
            </Button>
          ) : null}
          <Button
            variant="contained"
            onClick={onSave}
            disabled={saving || disabled}
            size={isMobile ? 'medium' : 'large'}
            sx={{ flex: { xs: 1, sm: 'none' }, textTransform: 'none', fontWeight: 600 }}
          >
            {saving ? 'Saving…' : saveLabel}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
